// @ts-nocheck

import { createSelector } from 'reselect';

import { MyMatchAPIErrorIds, MyMatchLocaleErrorIds } from '../constants/mymatch';
import {
  LineUpFormationsBySportId,
  SportsWithFixedFormation,
  SportsWithNoTab,
  SupportedLineUpSportIds,
  LineUpIceHockeyFormationByLine,
  SportsWithRaceTrackUI,
} from '../modules/line-up/constants/runtime';
import { type StateType } from '../states';
import { type BasketCachedMMAPIResponsesInfosEntityState } from '../states/basket';
import { type BasketItemState } from '../states/basket';
import { type BetState } from '../states/entities/bets';
import { type CategoryState } from '../states/entities/categories';
import { type CompetitorState } from '../states/entities/competitors';
import { MatchEntity } from '../states/entities/matches';
import { type OutcomeState } from '../states/entities/outcomes';
import { type PlayerState } from '../states/entities/players';
import { type SportState } from '../states/entities/sports';
import { type TeaserState } from '../states/entities/teasers';
import { type TournamentState } from '../states/entities/tournaments';
import { MyMatchBetStatuses } from '../states/mymatch';
import { type MyMatchBetStatus } from '../states/mymatch';
import { type MyMatchFutureEntitiesBets } from '../states/mymatch';
import { CardModes, type CardMode } from '../types/common';
import { MatchStatuses } from '../types/match';
import { type MatchScore } from '../types/match';
import { type Scorer } from '../types/match';
import { MatchViewModes } from '../types/matchView';
import { type MatchViewMode } from '../types/matchView';
import { type MyMatchAPIErrorId } from '../types/mymatch';
import { type MyMatchLocaleErrorId } from '../types/mymatch';
import { type SelectorMaker, type Selector } from '../types/selectors';
import { SportIds } from '../types/sports';
import { type TopMyMatch } from '../types/topMyMatch';
import { getIsBetValidFromFilterBetCategoryKind, getIsBetValidFromFilterPlayerKind } from '../utils/bet';
import { getImagePath } from '../utils/image';
import MatchUtils from '../utils/match';
import { createResultEqualSelector } from '../utils/reselect';
import isHome from '../utils/routes/isHome';

import {
  getFilterIdFromProps,
  getIsHotFilterSelectedFromProps,
  getMatchIdFromProps,
  getPathnameFromProps,
  getSportIdFromProps,
} from './_propsGetters_';
import { selectBasketCachedMMAPIResponsesInfos, selectBasketItems } from './basket/basketPrimary.selector';
import { selectBetBoostedOddCardMode } from './bet.selector';
import { selectEntitiesBets } from './entities/bets/entitiesBetsPrimary.selector';
import { selectEntitiesCategoriesEntity } from './entities/categories/entitiesCategoriesPrimary.selector';
import { selectEntitiesCompetitors } from './entities/competitors/entitiesCompetitorsPrimary.selector';
import {
  selectEntitiesMatches,
  selectEntitiesMatchesEntity,
  selectEntitiesMatchesEntityBets,
  selectEntitiesMatchesEntityCategoryId,
  selectEntitiesMatchesEntityImg,
  selectEntitiesMatchesEntityLineupsAvailable,
  selectEntitiesMatchesEntityMainBetId,
  selectEntitiesMatchesEntityMatchtime,
  selectEntitiesMatchesEntityScore,
  selectEntitiesMatchesEntitySportId,
  selectEntitiesMatchesEntityStartingOrdersAvailable,
  selectEntitiesMatchesEntityStatus,
  selectEntitiesMatchesEntityTopMymatchs,
  selectEntitiesMatchesEntityTournamentId,
} from './entities/matches/entitiesMatchesPrimary.selector';
import { type EntitiesMatchesSelectorProps } from './entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOdds } from './entities/odds/entitiesOddsPrimary.selector';
import { selectEntitiesOutcomes } from './entities/outcomes/entitiesOutcomesPrimary.selector';
import { selectEntitiesPlayers } from './entities/players/entitiesPlayersPrimary.selector';
import { selectEntitiesSportsEntity } from './entities/sports/entitiesSportsPrimary.selector';
import { selectEntitiesTeasers } from './entities/teasers/entitiesTeasersPrimary.selector';
import { selectEntitiesTournamentsEntity } from './entities/tournaments/entitiesTournamentsPrimary.selector';
import { selectFavoritesIds } from './favorites/favoritesPrimary.selector';
import {
  selectMyMatchFutureEntitiesBets,
  selectMyMatchGetFutureOddsIsLoadingMatchIds,
  selectMyMatchModeMatchIds,
} from './mymatch/mymatchPrimary.selector';

const emptyArray: [] = [];
const emptyObject = {};

type MatchSelectorProps = {
  matchId?: number;
  pathname?: string;
};

// no-memoize
export const selectMatchTeaserSelector: Selector<TeaserState | null> = (
  state: StateType,
  { matchId }: MatchSelectorProps = {},
) => selectEntitiesTeasers(state).find((teaser) => teaser.matchId === matchId) ?? null;

// no-memoize
export const selectMatchImage: Selector<string | null | undefined> = (
  state: StateType,
  props: MatchSelectorProps = {},
) => {
  const matchTeaser = selectMatchTeaserSelector(state, props);
  const matchImg = selectEntitiesMatchesEntityImg(state, props);
  const pathname = getPathnameFromProps(state, props);

  if (!pathname || isHome(pathname)) {
    let result = matchImg;

    if (matchTeaser) {
      const { img } = matchTeaser;
      result = img;
    }

    return getImagePath(result);
  }
};

// no-memoize
export const selectMatchMainBetEntity: Selector<BetState | undefined> = (
  state: StateType,
  props: MatchSelectorProps = {},
) => {
  const betsState = selectEntitiesBets(state);
  const matchMainBetId = selectEntitiesMatchesEntityMainBetId(state, props);

  return matchMainBetId ? betsState[matchMainBetId] : undefined;
};

// no-memoize
export const selectMatchMainBetEntityPreviousBoostedOdd: Selector<number | undefined> = (
  state: StateType,
  props: MatchSelectorProps = {},
) => selectMatchMainBetEntity(state, props)?.previousOdd;

export const makeMatchMainBetEntityOutcomeIdsSelector: SelectorMaker<Array<number>> = () =>
  createSelector([selectMatchMainBetEntity], (matchMainBet) => matchMainBet?.outcomes ?? emptyArray);

export const makeMatchMainBetOutcomesSelector: SelectorMaker<Array<OutcomeState>> = () =>
  createSelector(
    [makeMatchMainBetEntityOutcomeIdsSelector(), selectEntitiesOutcomes],
    (matchMainBetOutcomeIds, outcomesState) =>
      matchMainBetOutcomeIds.reduce(
        (acc, outcomeId) => (outcomesState[outcomeId] ? [...acc, outcomesState[outcomeId]] : acc),
        [],
      ),
  );

export const makeMatchMainBetEntityOutcomesWithValueSelector: SelectorMaker<
  Array<OutcomeState & { value: number }>
> = () =>
  createResultEqualSelector(
    [makeMatchMainBetOutcomesSelector(), selectEntitiesOdds],
    (matchMainBetOutcomes, oddsState) =>
      matchMainBetOutcomes.reduce(
        (acc, outcome) =>
          typeof oddsState[outcome.oddId] === 'number'
            ? [
                ...acc,
                {
                  ...outcome,
                  value: oddsState[outcome.oddId],
                },
              ]
            : acc,
        [],
      ),
  );

// no-memoize
export const selectMatchMainBetEntityMMStatus: Selector<MyMatchBetStatus> = (
  state: StateType,
  props: MatchSelectorProps = {},
) => {
  const mmFutureEntitiesBets = selectMyMatchFutureEntitiesBets(state);
  const mainBetId = selectEntitiesMatchesEntityMainBetId(state, props);

  return mmFutureEntitiesBets[mainBetId]?.mmStatus ?? MyMatchBetStatuses.INACTIVE;
};

// no-memoize
export const selectMatchMainBetEntityMultichoiceMMStatus: Selector<MyMatchBetStatus> = (
  state: StateType,
  props: MatchSelectorProps = {},
) => {
  const mmFutureEntitiesBets = selectMyMatchFutureEntitiesBets(state);
  const mainBetId = selectEntitiesMatchesEntityMainBetId(state, props);

  return mmFutureEntitiesBets[mainBetId]?.mmMultichoiceStatus ?? MyMatchBetStatuses.INACTIVE;
};

// no-memoize
export const selectIsMatchFavorite: Selector<boolean> = (state: StateType, { matchId }: MatchSelectorProps = {}) =>
  !!~selectFavoritesIds(state).indexOf(matchId);

// no-memoize
export const selectMatchCompetitor1NbRedCards: Selector<number> = (
  state: StateType,
  props: MatchSelectorProps = {},
) => {
  const match = selectEntitiesMatchesEntity(state, props);

  return (match?.redCardsHome ?? 0) + (match?.yellowRedCardsHome ?? 0);
};

// no-memoize
export const selectMatchCompetitor2NbRedCards: Selector<number> = (
  state: StateType,
  props: MatchSelectorProps = {},
) => {
  const match = selectEntitiesMatchesEntity(state, props);

  return (match?.redCardsAway ?? 0) + (match?.yellowRedCardsAway ?? 0);
};

// no-memoize
export const selectMatchHasStream: Selector<boolean> = (state: StateType, props?: EntitiesMatchesSelectorProps) =>
  MatchEntity.hasStream(selectEntitiesMatchesEntity(state, props));

// no-memoize
export const selectMatchHasHighlights: Selector<boolean> = (state: StateType, props?: EntitiesMatchesSelectorProps) =>
  MatchEntity.hasHighlights(selectEntitiesMatchesEntity(state, props));

export const makeMatchScoresSelector: SelectorMaker<MatchScore> = () =>
  createResultEqualSelector(selectEntitiesMatchesEntity, (match) => MatchEntity.getScores(match));

// no-memoize
export const selectMatchNbRedCards: Selector<number> = (state: StateType, props?: EntitiesMatchesSelectorProps) =>
  MatchEntity.getRedCards(selectEntitiesMatchesEntity(state, props));

// no use of factory here because top my matches are only selected inside match view
export const selectMatchTMM: Selector<Array<TopMyMatch>> = createResultEqualSelector(
  [selectEntitiesMatchesEntityTopMymatchs],
  (matchTopMymatchs) => matchTopMymatchs?.filter((k) => k.odds > 1) ?? emptyArray,
);

// no-memoize
export const selectMatchNbTMM: Selector<number> = (state: StateType, props?: EntitiesMatchesSelectorProps) =>
  selectMatchTMM(state, props).length;

// no-memoize
export const selectMatchHasTMM: Selector<boolean> = (state: StateType, props?: EntitiesMatchesSelectorProps) =>
  !!selectMatchNbTMM(state, props);

// no-memoize
export const selectIsMatchLive: Selector<boolean> = (state: StateType, props?: EntitiesMatchesSelectorProps) =>
  MatchEntity.isLive(selectEntitiesMatchesEntity(state, props));

// no-memoize
export const selectIsMatchEnded: Selector<boolean> = (state: StateType, props?: EntitiesMatchesSelectorProps) =>
  MatchEntity.isEnded(selectEntitiesMatchesEntity(state, props));

export const makeMatchBetsArraySelector: SelectorMaker<Array<BetState>> = () =>
  createResultEqualSelector([selectEntitiesMatchesEntityBets, selectEntitiesBets], (matchBetIds, betsState) =>
    matchBetIds.reduce((acc, betId) => (betsState[betId] ? [...acc, betsState[betId]] : acc), []),
  );

// no-memoize
export const selectMatchSportsEntity: Selector<SportState | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectEntitiesSportsEntity(state, { sportId: selectEntitiesMatchesEntitySportId(state, props) });

// no-memoize
export const selectMatchSportsEntitySportName: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchSportsEntity(state, props)?.sportName;

// no-memoize
export const selectMatchSportsEntityWarning: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchSportsEntity(state, props)?.warning;

// no-memoize
export const selectMatchSportsEntityPrematchPromo: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchSportsEntity(state, props)?.prematchPromo;

// no-memoize
export const selectMatchSportsEntityLivePromo: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchSportsEntity(state, props)?.livePromo;

// no-memoize
export const selectMatchCategoriesEntity: Selector<CategoryState | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectEntitiesCategoriesEntity(state, { categoryId: selectEntitiesMatchesEntityCategoryId(state, props) });

// no-memoize
export const selectMatchCategoriesEntityCategoryName: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchCategoriesEntity(state, props)?.categoryName;

// no-memoize
export const selectMatchCategoriesEntityWarning: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchCategoriesEntity(state, props)?.warning;

// no-memoize
export const selectMatchCategoriesEntityFlag: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchCategoriesEntity(state, props)?.flag;

// no-memoize
export const selectMatchCategoriesEntityPrematchPromo: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchCategoriesEntity(state, props)?.prematchPromo;

// no-memoize
export const selectMatchCategoriesEntityLivePromo: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchCategoriesEntity(state, props)?.livePromo;

// no-memoize
export const selectMatchTournamentsEntity: Selector<TournamentState | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectEntitiesTournamentsEntity(state, { tournamentId: selectEntitiesMatchesEntityTournamentId(state, props) });

// no-memoize
export const selectMatchTournamentsEntityTournamentName: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchTournamentsEntity(state, props)?.tournamentName;

// no-memoize
export const selectMatchTournamentsEntityWarning: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchTournamentsEntity(state, props)?.warning;

// no-memoize
export const selectMatchTournamentsEntityTopName: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchTournamentsEntity(state, props)?.topName;

// no-memoize
export const selectMatchTournamentsEntityFlag: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchTournamentsEntity(state, props)?.flag;

// no-memoize
export const selectMatchTournamentsEntityPrematchPromo: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchTournamentsEntity(state, props)?.prematchPromo;

// no-memoize
export const selectMatchTournamentsEntityLivePromo: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchTournamentsEntity(state, props)?.livePromo;

// no-memoize
export const selectMatchTournamentsEntityDisplayCupRoster: Selector<boolean | null | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchTournamentsEntity(state, props)?.displayCupRoster;

// no-memoize
export const selectMatchTournamentsEntityDisplayLiveTable: Selector<boolean | null | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchTournamentsEntity(state, props)?.displayLiveTable;

// no-memoize
export const selectMatchTournamentsEntitySrSeasonId: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchTournamentsEntity(state, props)?.srSeasonId;

// no-memoize
export const selectMatchTournamentsEntitySrTournamentId: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => selectMatchTournamentsEntity(state, props)?.srTournamentId;

// no-memoize
export const selectMatchHasTracker: Selector<boolean> = (state: StateType, props?: EntitiesMatchesSelectorProps) =>
  MatchEntity.hasTracker(selectEntitiesMatchesEntity(state, props), selectMatchTournamentsEntity(state, props));

// no-memoize
export const selectMatchTimeInSeconds: Selector<number | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => {
  const matchTime = selectEntitiesMatchesEntityMatchtime(state, props);
  return matchTime && typeof matchTime === 'string' ? MatchUtils.convertMatchTimeToSecond(matchTime) : undefined;
};

// no-memoize
export const selectMatchFlagId: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => {
  const tournamentFlagId = selectMatchTournamentsEntityFlag(state, props);
  const categoryFlagId = selectMatchCategoriesEntityFlag(state, props);

  if (tournamentFlagId) {
    return tournamentFlagId;
  }

  if (categoryFlagId) {
    return categoryFlagId;
  }
};

// no-memoize
export const selectOutrightMatchFlagId: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => {
  const categoryFlagId = selectMatchCategoriesEntityFlag(state, props);
  const tournamentFlagId = selectMatchTournamentsEntityFlag(state, props);

  if (categoryFlagId) {
    return categoryFlagId;
  }

  if (tournamentFlagId) {
    return tournamentFlagId;
  }
};

// no-memoize
export const selectMatchCardMode: Selector<CardMode> = (state: StateType, props?: EntitiesMatchesSelectorProps) => {
  const match = selectEntitiesMatchesEntity(state, props);

  if (match && match.sportId === SportIds.SPECIAL_ODDS) {
    return selectBetBoostedOddCardMode(state, { betId: match.mainBetId });
  }

  return CardModes.NORMAL;
};

// no-memoize
export const selectMatchTries: Selector<Array<Scorer>> = (state: StateType, props?: EntitiesMatchesSelectorProps) => {
  const match = selectEntitiesMatchesEntity(state, props);

  if (match) {
    return MatchEntity.getTries(match);
  }

  // $FlowFixMe
  return emptyArray;
};

// no-memoize
export const selectMatchHomeScore: Selector<number> = (state: StateType, props?: EntitiesMatchesSelectorProps) =>
  parseInt(selectEntitiesMatchesEntityScore(state, props)?.split(':')?.[0] ?? 0, 10);

// no-memoize
export const selectMatchAwayScore: Selector<number> = (state: StateType, props?: EntitiesMatchesSelectorProps) =>
  parseInt(selectEntitiesMatchesEntityScore(state, props)?.split(':')?.[1] ?? 0, 10);

// no-memoize
export const selectMatchTeamCompetitorId: Selector<number | null | undefined> = (
  state: StateType,
  props: (EntitiesMatchesSelectorProps & { teamId?: number }) | undefined,
) => {
  const { teamId, ...restProps } = props ?? {};

  return MatchEntity.getCompetitorId(selectEntitiesMatchesEntity(state, restProps), teamId);
};

// no-memoize
export const selectMatchTeamCompetitorEntity: Selector<CompetitorState | undefined> = (
  state: StateType,
  props: (EntitiesMatchesSelectorProps & { teamId?: number }) | undefined,
) => {
  const competitors = selectEntitiesCompetitors(state);
  const matchTeamCompetitorId = selectMatchTeamCompetitorId(state, props);

  if (matchTeamCompetitorId) {
    return competitors[matchTeamCompetitorId];
  }
};

// no use of factory here because it is only used inside match tool bar
export const selectFirstMatchIdInMatchTournament: Selector<number | undefined> = createSelector(
  [selectEntitiesMatches, selectEntitiesMatchesEntityTournamentId],
  // $FlowFixMe
  (matches, matchTournamentId) => Object.values(matches).find((m) => m?.tournamentId === matchTournamentId)?.matchId,
);

// no-memoize
export const selectMatchViewMode: Selector<MatchViewMode> = (
  state: StateType,
  { matchId }: EntitiesMatchesSelectorProps = {},
) => {
  const myMatchModeMatchIds = selectMyMatchModeMatchIds(state);

  return ~myMatchModeMatchIds.indexOf(matchId) ? MatchViewModes.MY_MATCH : MatchViewModes.CLASSIC;
};

export const makeMatchCompetitorIdsSelector: SelectorMaker<Array<number>> = () =>
  createResultEqualSelector(
    [selectEntitiesMatchesEntity],
    (match): Array<number> => [match?.competitor1Id, match?.competitor2Id].filter((k) => k),
  );

export const makeMatchCompetitorsSelector: SelectorMaker<Array<CompetitorState>> = () =>
  createResultEqualSelector(
    [makeMatchCompetitorIdsSelector(), selectEntitiesCompetitors],
    (competitorIds, competitors): Array<CompetitorState> =>
      competitorIds.reduce(
        (acc, competitorId) => (competitors[competitorId] ? [...acc, competitors[competitorId]] : acc),
        [],
      ),
  );

export const makeMatchCachedMMAPIResponsesInfosEntitySelector: SelectorMaker<
  BasketCachedMMAPIResponsesInfosEntityState
> = () =>
  createResultEqualSelector(
    [getMatchIdFromProps, selectBasketCachedMMAPIResponsesInfos],
    (matchId, cachedMMAPIResponsesInfos) => cachedMMAPIResponsesInfos[matchId] ?? emptyObject,
  );

export const makeMatchMMErrorIdFromCacheSelector: SelectorMaker<MyMatchAPIErrorId | undefined> = () =>
  createSelector(
    [makeMatchCachedMMAPIResponsesInfosEntitySelector()],
    (matchcachedMMAPIResponsesInfos) => matchcachedMMAPIResponsesInfos.result?.mymatchError?.errorId,
  );

// no-memoize
export const selectIsMatchGetFutureOddsLoading: Selector<boolean> = (
  state: StateType,
  { matchId }: EntitiesMatchesSelectorProps = {},
) => !!~selectMyMatchGetFutureOddsIsLoadingMatchIds(state).indexOf(matchId);

// no-memoize
export const selectIsMatchMMLoading: Selector<boolean> = (state: StateType, props?: EntitiesMatchesSelectorProps) => {
  const matchViewMode = selectMatchViewMode(state, props);
  const isMatchGetFutureOddsLoading = selectIsMatchGetFutureOddsLoading(state, props);

  return matchViewMode === MatchViewModes.MY_MATCH && isMatchGetFutureOddsLoading;
};

export const makeBasketMatchItemsArraySelector: SelectorMaker<Array<BasketItemState>> = () =>
  createResultEqualSelector(
    [getMatchIdFromProps, selectBasketItems],
    (matchId, basketItems): Array<BasketItemState> =>
      Object.keys(basketItems).reduce(
        (acc, itemId) => (basketItems[itemId]?.matchId === matchId ? [...acc, basketItems[itemId]] : acc),
        [],
      ),
  );

export const makeIsAnyOfBasketMatchItemsCashoutingSelector: SelectorMaker<boolean> = () =>
  createSelector([makeBasketMatchItemsArraySelector()], (basketMatchItems: Array<BasketItemState>) =>
    basketMatchItems.some((item) => !!item.cashout),
  );

export const makeNbBasketMatchSelectionsSelector: SelectorMaker<number> = () =>
  createSelector([makeBasketMatchItemsArraySelector()], (basketMatchItems: Array<BasketItemState>) =>
    basketMatchItems.reduce((acc, matchItem) => {
      const { selectionItems } = matchItem;

      if (Array.isArray(selectionItems)) {
        return (
          acc +
          selectionItems.reduce((_acc, selectionItem) => {
            const { outcomeIds } = selectionItem;

            if (Array.isArray(outcomeIds)) {
              return acc + outcomeIds.length;
            }

            return acc + 1;
          }, 0)
        );
      }

      return acc + 1;
    }, 0),
  );

export const makeBasketMatchSelectionsPredominantErrorIdSelector: SelectorMaker<MyMatchAPIErrorId> = () =>
  createSelector(
    [selectMyMatchFutureEntitiesBets, makeBasketMatchItemsArraySelector()],
    (futureBets: MyMatchFutureEntitiesBets, basketMatchItems: Array<BasketItemState>) => {
      const MMErrorIdsByMMStatuses = {
        [MyMatchBetStatuses.SUSPENDED]: MyMatchAPIErrorIds.SELECTION_SUSPENDED,
        [MyMatchBetStatuses.INACTIVE]: MyMatchAPIErrorIds.SELECTION_INACTIVE,
      };

      const mmStatusesByPriority = [MyMatchBetStatuses.SUSPENDED, MyMatchBetStatuses.INACTIVE];

      let predominantMMStatus = MyMatchBetStatuses.SUSPENDED;
      let hasBeenSet = false;

      const handleItemPredominantMMStatusAffectation = (item) => {
        const { mmStatus: itemMMStatus, mmMultichoiceStatus: itemMultichoiceMMStatus } = futureBets[item.betId] ?? {};

        if (itemMMStatus || itemMultichoiceMMStatus) {
          const mmStatus = itemMultichoiceMMStatus ?? itemMMStatus;
          const priorityLevel = mmStatusesByPriority.indexOf(mmStatus);
          const predominantPriorityLevel = mmStatusesByPriority.indexOf(predominantMMStatus);

          if (~priorityLevel && ~predominantPriorityLevel && priorityLevel > predominantPriorityLevel) {
            predominantMMStatus = mmStatus;
            hasBeenSet = true;
          }
        }
      };

      for (const basketMatchItem of basketMatchItems) {
        const { selectionItems } = basketMatchItem;

        if (Array.isArray(selectionItems)) {
          for (const selectionItem of selectionItems) {
            handleItemPredominantMMStatusAffectation(selectionItem);
          }
        } else {
          handleItemPredominantMMStatusAffectation(basketMatchItem);
        }
      }

      if (hasBeenSet) {
        return MMErrorIdsByMMStatuses[predominantMMStatus];
      }
    },
  );

// no-memoize
export const selectIsMatchCachedMMAPIResponsesInfosValid: Selector<boolean> = (
  state: StateType,
  { matchId }: EntitiesMatchesSelectorProps = {},
) => {
  const cachedMMAPIResponsesInfos = selectBasketCachedMMAPIResponsesInfos(state);
  const hasMatchCache = !!cachedMMAPIResponsesInfos[matchId];

  if (hasMatchCache) {
    const basketItems = selectBasketItems(state);
    const itemIds = Object.keys(basketItems);
    const items = itemIds.map((itemId) => basketItems[itemId]);

    return !!items.length && !!items.find((item) => item.matchId === matchId);
  }

  return true;
};

export const makeMatchMMErrorIdSelector: SelectorMaker<(MyMatchAPIErrorId & MyMatchLocaleErrorId) | undefined> = () =>
  createSelector(
    [
      selectIsMatchCachedMMAPIResponsesInfosValid,
      makeMatchMMErrorIdFromCacheSelector(),
      makeIsAnyOfBasketMatchItemsCashoutingSelector(),
    ],
    (isMatchCachedMMAPIResponsesInfosValid, matchMMErrorId, wasMatchAlreadyHereBeforeCompletion) => {
      if (isMatchCachedMMAPIResponsesInfosValid) {
        if (wasMatchAlreadyHereBeforeCompletion) {
          return MyMatchLocaleErrorIds.MATCH_WAS_ALREADY_HERE_BEFORE_COMPLETION;
        }

        return matchMMErrorId;
      }
    },
  );

// no-memoize
export const selectMatchTeamFormation: Selector<string> = (
  state: StateType,
  props: (EntitiesMatchesSelectorProps & { teamId?: number; sportId?: number }) | undefined,
) => {
  const { sportId, ...restProps } = props ?? {};
  const { teamId } = restProps;

  const competitor = selectMatchTeamCompetitorEntity(state, props);

  if (teamId && sportId === SportIds.ICEHOCKEY) {
    return LineUpIceHockeyFormationByLine[teamId];
  }

  if (SportsWithFixedFormation.indexOf(Number(sportId)) !== -1) {
    return LineUpFormationsBySportId[sportId];
  }

  return competitor?.formationType ?? '4-3-3';
};

export const makeMatchTeamCompetitorPlayerIdsSelector: SelectorMaker<Array<number>> = () =>
  createResultEqualSelector(
    [selectMatchTeamCompetitorEntity, makeMatchCompetitorsSelector(), getSportIdFromProps],
    (competitor, competitors, sportId): Array<number> => {
      if (SportsWithNoTab.indexOf(Number(sportId)) !== -1 || sportId === SportIds.ICEHOCKEY) {
        return competitors.reduce((acc, compet) => (compet.players ? [...acc, ...compet.players] : acc), emptyArray);
      }

      return competitor?.players ?? emptyArray;
    },
  );

export const makeMatchTeamCompetitorPlayersArraySelector: SelectorMaker<Array<PlayerState>> = () =>
  createResultEqualSelector(
    [makeMatchTeamCompetitorPlayerIdsSelector(), selectEntitiesPlayers],
    (playerIds, playersMap): Array<PlayerState> =>
      playerIds.reduce((acc, playerId) => (playersMap[playerId] ? [...acc, playersMap[playerId]] : acc), []),
  );

// no-memoize
export const selectIsMatchSportLineUpCompatible: Selector<boolean> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => !!~SupportedLineUpSportIds.indexOf(selectEntitiesMatchesEntitySportId(state, props));

export const selectIsMatchLineUpAvailableWithPartialData: Selector<boolean> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => {
  const matchLineUpsAvailable = selectEntitiesMatchesEntityLineupsAvailable(state, props);
  const startingOrdersAvailable = selectEntitiesMatchesEntityStartingOrdersAvailable(state, props);
  const sportId = selectEntitiesMatchesEntitySportId(state, props);
  const isMatchSportLineUpCompatible = selectIsMatchSportLineUpCompatible(state, props);
  const matchStatus = selectEntitiesMatchesEntityStatus(state, props);

  return (
    ((SportsWithRaceTrackUI.indexOf(sportId) !== -1 && startingOrdersAvailable) || matchLineUpsAvailable) &&
    isMatchSportLineUpCompatible &&
    !!matchStatus &&
    matchStatus.toLowerCase() !== MatchStatuses.CANCELLED
  );
};

export const makeIsMatchLineUpAvailableWithFullDataSelector: SelectorMaker<boolean> = () =>
  createSelector(
    [selectIsMatchLineUpAvailableWithPartialData, makeMatchCompetitorsSelector(), selectEntitiesMatchesEntitySportId],
    (isMatchLineUpAvailableWithPartialData, competitors, sportId) => {
      return (
        isMatchLineUpAvailableWithPartialData &&
        (SportsWithRaceTrackUI.indexOf(sportId) !== -1 ||
          competitors?.some((competitor) => !!competitor.lineupAvailable))
      );
    },
  );

// no-memoize
export const selectMatchSportPromo: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => {
  const isLive = selectIsMatchLive(state, props);

  if (isLive) {
    return selectMatchSportsEntityLivePromo(state, props);
  }

  return selectMatchSportsEntityPrematchPromo(state, props);
};

// no-memoize
export const selectMatchCategoryPromo: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => {
  const isLive = selectIsMatchLive(state, props);

  if (isLive) {
    return selectMatchCategoriesEntityLivePromo(state, props);
  }

  return selectMatchCategoriesEntityPrematchPromo(state, props);
};

// no-memoize
export const selectMatchTournamentPromo: Selector<string | undefined> = (
  state: StateType,
  props?: EntitiesMatchesSelectorProps,
) => {
  const isLive = selectIsMatchLive(state, props);

  if (isLive) {
    return selectMatchTournamentsEntityLivePromo(state, props);
  }

  return selectMatchTournamentsEntityPrematchPromo(state, props);
};

export const makeComplexeMatchBetsSelector: SelectorMaker<Array<{ bet: BetState; isMultichoice: boolean }>> = () =>
  createResultEqualSelector(
    [
      selectEntitiesMatchesEntityBets,
      selectEntitiesBets,
      selectMatchViewMode,
      getFilterIdFromProps,
      selectEntitiesPlayers,
      selectEntitiesOutcomes,
      selectMyMatchFutureEntitiesBets,
      getIsHotFilterSelectedFromProps,
    ],
    (betIds, bets, matchViewMode, filterId, players, outcomes, mmBets, isHotFilterSelected) => {
      const prefilteredMatchBets = (() => {
        if (matchViewMode === MatchViewModes.MY_MATCH) {
          return betIds.reduce(
            (acc, betId) =>
              bets[betId] && bets[betId].mmStatus !== MyMatchBetStatuses.INACTIVE ? [...acc, bets[betId]] : acc,
            [],
          );
        }

        return betIds.reduce((acc, betId) => (bets[betId] ? [...acc, bets[betId]] : acc), []);
      })();

      return prefilteredMatchBets.reduce((acc, bet) => {
        let isBetValid = true;

        if (filterId) {
          const isBetValidFromFilterBetCategoryKind = getIsBetValidFromFilterBetCategoryKind(bet, filterId);
          const isBetValidFromFilterPlayerKind = getIsBetValidFromFilterPlayerKind(bet, filterId, players, outcomes);

          isBetValid = isBetValid && isBetValidFromFilterBetCategoryKind && isBetValidFromFilterPlayerKind;
        }

        const { outcomes: outcomeIds } = bet;

        if (isHotFilterSelected) {
          if (Array.isArray(outcomeIds)) {
            const isBetValidFromHotFilter = outcomeIds.some((outcomeId) => outcomes[outcomeId]?.hotUsers);

            isBetValid = isBetValid && isBetValidFromHotFilter;
          }
        }

        if (!isBetValid) {
          return acc;
        }

        const { mmStatus = MyMatchBetStatuses.INACTIVE, mmMultichoiceStatus = MyMatchBetStatuses.INACTIVE } =
          mmBets[bet.betId] ?? {};

        const isMMStatusValid =
          !matchViewMode ||
          matchViewMode === MatchViewModes.CLASSIC ||
          (matchViewMode === MatchViewModes.MY_MATCH && mmStatus && mmStatus !== MyMatchBetStatuses.INACTIVE) ||
          (mmMultichoiceStatus && mmMultichoiceStatus !== MyMatchBetStatuses.INACTIVE);

        if (!isMMStatusValid) {
          return acc;
        }

        if (
          matchViewMode === MatchViewModes.MY_MATCH &&
          (mmStatus === MyMatchBetStatuses.ACTIVE || mmStatus === MyMatchBetStatuses.SUSPENDED) &&
          (mmMultichoiceStatus === MyMatchBetStatuses.ACTIVE || mmMultichoiceStatus === MyMatchBetStatuses.SUSPENDED)
        ) {
          return [...acc, { bet, isMultichoice: false }, { bet, isMultichoice: true }];
        }

        if (
          !mmStatus &&
          (mmMultichoiceStatus === MyMatchBetStatuses.ACTIVE || mmMultichoiceStatus === MyMatchBetStatuses.SUSPENDED)
        ) {
          return [...acc, { bet, isMultichoice: true }];
        }

        return [...acc, { bet, isMultichoice: false }];
      }, []);
    },
  );

// no-memoize
export const selectIsMatchMultimatch: Selector<boolean> = (state: StateType, props: MatchSelectorProps = {}) =>
  MatchEntity.isMultimatch(selectEntitiesMatchesEntity(state, props));
